import { useEffect, useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { ROLE_LABELS } from '../../services/userService'
import NotificationBell from './NotificationBell'
import {
  HiOutlineViewGrid, HiOutlineClipboardList, HiOutlineUsers, HiOutlineUserGroup,
  HiOutlineAdjustments, HiOutlineColorSwatch, HiOutlineMenuAlt2, HiOutlineChartBar,
  HiOutlineTemplate, HiOutlineLogout, HiOutlineMenu, HiOutlineX, HiOutlineTrash, HiOutlineIdentification,
} from 'react-icons/hi'
import clsx from 'clsx'

const ALL = ['admin', 'hr_manager', 'team_lead', 'member']
const MANAGER = ['admin', 'hr_manager', 'team_lead']   // Quản lý + Admin (D1: 1 tầng phẳng)

const NAV = [
  { to: '/admin', label: 'Tổng quan', icon: HiOutlineViewGrid, end: true, roles: ALL },
  { to: '/admin/leads', label: 'Hồ sơ ứng viên', icon: HiOutlineClipboardList, roles: ALL },
  { to: '/admin/report', label: 'Báo cáo', icon: HiOutlineChartBar, roles: MANAGER },
  { to: '/admin/deletion-logs', label: 'Lịch sử xóa', icon: HiOutlineTrash, roles: MANAGER },
  { to: '/admin/forms', label: 'Chiến dịch tuyển dụng', icon: HiOutlineTemplate, roles: ALL },
  { to: '/admin/teams', label: 'Nhóm nhân sự', icon: HiOutlineUserGroup, roles: MANAGER },
  { to: '/admin/users', label: 'Phân quyền', icon: HiOutlineUsers, roles: MANAGER },
  { to: '/admin/settings', label: 'Cấu hình site', icon: HiOutlineColorSwatch, roles: ['admin'] },
  { to: '/admin/menus', label: 'Menu điều hướng', icon: HiOutlineMenuAlt2, roles: ['admin'] },
  { to: '/admin/options', label: 'Cấu hình form', icon: HiOutlineAdjustments, roles: ['admin'] },
  { to: '/admin/sso', label: 'Cấu hình SSO', icon: HiOutlineIdentification, roles: ['admin'] },
]

const AVATAR_COLORS = [
  'bg-primary-600',
  'bg-blue-600',
  'bg-emerald-600',
  'bg-violet-600',
  'bg-rose-600',
  'bg-amber-600',
  'bg-cyan-600',
  'bg-slate-700',
]

function getAvatarInitials(nameOrEmail) {
  const raw = typeof nameOrEmail === 'string' ? nameOrEmail.trim() : ''
  if (!raw) return 'U'

  const base = raw.includes('@') ? raw.split('@')[0] : raw
  const parts = base.split(/[\s._-]+/).filter(Boolean)
  if (!parts.length) return 'U'

  if (parts.length > 1) {
    return parts
      .slice(-2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase()
  }

  return parts[0].slice(0, 2).toUpperCase()
}

function getAvatarColor(nameOrEmail) {
  const value = typeof nameOrEmail === 'string' && nameOrEmail.trim() ? nameOrEmail.trim() : 'User'
  let hash = 0

  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0
  }

  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

function UserAvatar({ avatarUrl, displayName }) {
  const initials = getAvatarInitials(displayName)
  const colorClass = getAvatarColor(displayName)

  return (
    <div className={clsx('avatar w-9 h-9 text-sm overflow-hidden flex-shrink-0 text-white font-semibold', !avatarUrl && colorClass)}>
      {avatarUrl ? (
        <img src={avatarUrl} alt={displayName || 'User'} className="w-full h-full object-cover" />
      ) : (
        initials
      )}
    </div>
  )
}

function AdminLayout() {
  const navigate = useNavigate()
  const { user, logout, refreshUser } = useAuthStore()
  const [open, setOpen] = useState(false)

  // Đồng bộ vai trò/nhóm từ server (đổi quyền có hiệu lực ngay, không cần đăng xuất):
  // khi vào trang + khi quay lại tab (tối đa 1 lần/phút)
  useEffect(() => {
    let last = 0
    const sync = () => {
      if (Date.now() - last < 60000) return
      last = Date.now()
      refreshUser()
    }
    sync()
    const onVisible = () => { if (document.visibilityState === 'visible') sync() }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', sync)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', sync)
    }
  }, [refreshUser])

  const avatarUrl = user?.avatar_url || user?.sso_avatar
  const displayName = user?.sso_name || user?.full_name || user?.email || 'User'
  const displayTitle = user?.display_title || user?.sso_title || ROLE_LABELS[user?.role] || user?.role
  const displayUnit = user?.sso_unit

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  const visibleNav = NAV.filter((n) => n.roles.includes(user?.role))

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-secondary-100">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-primary-600 text-white flex items-center justify-center font-bold">QT</div>
          <div>
            <div className="font-semibold text-secondary-900 leading-tight">Quản Lý Tuyển Dụng</div>
            <div className="text-2xs text-secondary-400">Recruitment Admin</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {visibleNav.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setOpen(false)}
            className={({ isActive }) => (isActive ? 'nav-item-active' : 'nav-item')}
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-secondary-100">
        <div className="flex items-center gap-3 px-2 py-2 mb-2">
          <UserAvatar avatarUrl={avatarUrl} displayName={displayName} />
          <div className="min-w-0">
            <div className="text-sm font-medium text-secondary-900 truncate">{displayName}</div>
            <div className="text-2xs text-secondary-400 truncate">{displayTitle}</div>
            {displayUnit && <div className="text-2xs text-secondary-400 truncate">{displayUnit}</div>}
          </div>
        </div>
        <button onClick={handleLogout} className="nav-item w-full text-red-600 hover:bg-red-50 hover:text-red-700">
          <HiOutlineLogout className="w-5 h-5" />
          Đăng xuất
        </button>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:w-64 bg-white border-r border-secondary-100 z-30">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-white shadow-xl animate-slide-up">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Content */}
      <div className="md:pl-64">
        {/* Topbar (desktop + mobile) — chuông thông báo góc phải */}
        <header className="sticky top-0 z-20 bg-white border-b border-secondary-100 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => setOpen(true)} className="md:hidden p-2 -ml-2 text-secondary-600">
              <HiOutlineMenu className="w-6 h-6" />
            </button>
            <span className="font-semibold text-secondary-900 md:hidden">Quản Lý Tuyển Dụng</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 min-w-0">
              <UserAvatar avatarUrl={avatarUrl} displayName={displayName} />
              <div className="min-w-0">
                <div className="text-sm font-medium text-secondary-900 truncate">{displayName}</div>
                <div className="text-2xs text-secondary-400 truncate">{displayTitle}</div>
              </div>
            </div>
            <NotificationBell />
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default AdminLayout
